import { Fragment } from 'react';

import { DateRangeParameter, DateTimeRangeParameter, isDateTimeRangeParameter } from '@drivingdoc/shared-types/reports';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useFormContext } from 'react-hook-form';

import DatePickerInput from 'components/FormInputs/DatePickerInput';
import DateTimeInput from 'components/Forms/DateTimeInput';

import { calculateDateFromOffset, calculatePresetDateRange, getPresetLabel } from 'utils/reportParameters';

import { resolveMaxDate } from './resolveMaxDate';

interface DateRangeParameterFieldProps {
  parameter: DateRangeParameter | DateTimeRangeParameter;
}

export default function DateRangeParameterField({ parameter }: DateRangeParameterFieldProps) {
  const { setValue, watch } = useFormContext();
  const { config } = parameter;
  const presets = config?.presets ?? [];
  const isDateTime = isDateTimeRangeParameter(parameter);

  const currentStart = watch(`${parameter.name}.start`) as string | undefined;
  const currentEnd = watch(`${parameter.name}.end`) as string | undefined;

  const minDate = config?.minDateOffset ? calculateDateFromOffset(config.minDateOffset) : undefined;
  const maxDate = resolveMaxDate(config?.maxDateOffset, config?.allowFutureDates);

  const DateComponent = isDateTime ? DateTimeInput : DatePickerInput;

  return (
    <Fragment>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DateComponent
          name={`${parameter.name}.start`}
          label={`${parameter.label} Start`}
          minDate={minDate}
          maxDate={maxDate}
          clearable={!parameter.required}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DateComponent
          name={`${parameter.name}.end`}
          label={`${parameter.label} End`}
          minDate={minDate}
          maxDate={maxDate}
          clearable={!parameter.required}
        />
      </Grid>
      {presets.length > 0 && (
        <Grid size={12}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {presets.map((preset) => {
              const { start, end } = calculatePresetDateRange(preset);
              const formattedStart = start?.format('YYYY-MM-DD') ?? null;
              const formattedEnd = end?.format('YYYY-MM-DD') ?? null;
              const isActive =
                (currentStart ?? null) === formattedStart && (currentEnd ?? null) === formattedEnd;

              return (
                <Chip
                  key={preset}
                  label={getPresetLabel(preset)}
                  size="small"
                  variant={isActive ? 'filled' : 'outlined'}
                  color={isActive ? 'primary' : 'default'}
                  onClick={() => {
                    // Cast needed because react-hook-form can't resolve dynamic nested paths on Record<string, unknown>
                    const setField = setValue as (name: string, value: unknown) => void;
                    setField(`${parameter.name}.start`, formattedStart);
                    setField(`${parameter.name}.end`, formattedEnd);
                  }}
                />
              );
            })}
          </Stack>
        </Grid>
      )}
    </Fragment>
  );
}
